// Powłoka aplikacji (L-1..L-16): header (logo + klaster konta) + sidebar (nawigacja) +
// obszar treści na washu. Header i sidebar przezroczyste, rozgraniczone hairline'ami.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { fireLogoConfetti } from "../lib/logoConfetti";
import { IconLogout, IconSun, IconMoon, IconChevronDown, IconPhoneCall, IconMail } from "./ui/icons";

export type NavItem = { id: string; label: string; icon: ReactNode };

function avatarInitials(username: string): string {
  const words = (username || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function ThemeIcons() {
  return (
    <>
      <IconSun className="ic-sun" />
      <IconMoon className="ic-moon" />
    </>
  );
}

export function AppShell({
  nav,
  activeId,
  onSelect,
  onToggleTheme,
  onLogout,
  username = "admin",
  appName,
  children,
}: {
  nav: NavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  // Motyw nie jest tu potrzebny — ikona słońca/księżyca przełącza się przez CSS
  // na podstawie atrybutu data-theme na <html>, nie przez propsa.
  onToggleTheme: () => void;
  onLogout: () => void;
  username?: string;
  /** Nazwa automatyzacji obok logo (tylko desktop). Brak = pomarańczowy placeholder dla programisty. */
  appName?: string;
  children: ReactNode;
}) {
  const [acctOpen, setAcctOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const acctRef = useRef<HTMLDivElement>(null);
  const acctBtnRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const initials = avatarInitials(username);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (acctOpen && !acctRef.current?.contains(t)) setAcctOpen(false);
      if (
        sidebarOpen &&
        !sidebarRef.current?.contains(t) &&
        !burgerRef.current?.contains(t)
      )
        setSidebarOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (acctOpen) {
        setAcctOpen(false);
        acctBtnRef.current?.focus();
      } else if (sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [acctOpen, sidebarOpen]);

  // Typ opisany strukturalnie, a nie przez React.MouseEvent — typy Reacta nie są
  // w tym projekcie zainstalowane (patrz komentarz przy no-undef w eslint.config.js).
  const handleLogoClick = (e: { clientX: number; clientY: number }) => {
    fireLogoConfetti({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <button
            ref={burgerRef}
            type="button"
            className={`hbtn hbtn--icon hamburger${sidebarOpen ? " is-open" : ""}`}
            aria-label="Menu nawigacji"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <span className="hamburger__bars">
              <span />
              <span />
              <span />
            </span>
          </button>
          <button type="button" className="brand" onClick={handleLogoClick} aria-label="urmate.ai">
            <img src="/urmate-logo.webp" alt="urmate.ai" />
          </button>
          <span className={`brand-name${appName ? "" : " brand-name--missing"}`}>
            {appName ?? "[podaj nazwę automatyzacji]"}
          </span>
        </div>

        <div className="header-right">
          <button
            type="button"
            className="hbtn hbtn--icon bar-theme"
            aria-label="Przełącz motyw"
            onClick={onToggleTheme}
          >
            <ThemeIcons />
          </button>
          <button type="button" className="hbtn hbtn--text bar-logout" onClick={onLogout}>
            <IconLogout size={16} />
            Wyloguj
          </button>

          <div className="acct" ref={acctRef}>
            <button
              ref={acctBtnRef}
              type="button"
              className="hbtn acct-trigger"
              aria-haspopup="menu"
              aria-expanded={acctOpen}
              onClick={() => setAcctOpen((v) => !v)}
            >
              <span className="acct-initials">{initials}</span>
              <span className="acct-name">{username}</span>
              <IconChevronDown className="acct-chev" size={16} />
            </button>

            <div className={`acct-menu${acctOpen ? " is-open" : ""}`} role="menu" aria-label="Menu konta">
              <div className="acct-menu__head">
                <span className="acct-menu__avatar">{initials}</span>
                <div>
                  <p className="acct-menu__name">{username}</p>
                  <p className="acct-menu__role">Panel callbota</p>
                </div>
              </div>
              <hr className="acct-menu__divider" />

              <button
                type="button"
                className="acct-menu__item menu-theme"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleTheme();
                }}
              >
                <span className="theme-ic" aria-hidden="true">
                  <ThemeIcons />
                </span>
                Zmień motyw
              </button>

              <button
                type="button"
                className="acct-menu__item menu-logout"
                role="menuitem"
                onClick={() => {
                  setAcctOpen(false);
                  onLogout();
                }}
              >
                <IconLogout />
                Wyloguj
              </button>

              <hr className="acct-menu__divider acct-menu__divider--phone" />

              <div className="acct-menu__contact">
                <p className="acct-menu__contact-label">Pomoc / kontakt</p>
                <a href="tel:+48509891745">
                  <IconPhoneCall />
                  +48 509 891 745
                </a>
                <a href="mailto:hi@urmate.ai">
                  <IconMail />
                  hi@urmate.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      <aside
        ref={sidebarRef}
        id="app-sidebar"
        className={`app-sidebar${sidebarOpen ? " is-open" : ""}`}
      >
        <nav className="app-nav" aria-label="Nawigacja główna">
          {nav.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`nav-item${item.id === activeId ? " active" : ""}`}
              aria-current={item.id === activeId ? "page" : undefined}
              onClick={() => {
                onSelect(item.id);
                setSidebarOpen(false);
              }}
            >
              <span className="nav-ico" aria-hidden="true">
                {item.icon}
              </span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="scroll-fade" aria-hidden="true" />

      <main className="app-main">
        <div className="app-main__inner">{children}</div>
      </main>
    </>
  );
}
